import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Loader2, Plus, Trash2, Pencil, Users, FlaskConical, Play, Pause, Mail, ChevronDown, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { NET_COLLECTIONS, NET_STATUSES } from './collections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_STEP = { delay_days: 0, subject: '', body_html: '' };
const ENROLL_STATUS = {
    active: { label: 'En cours', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    completed: { label: 'Terminée', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    stopped: { label: 'Arrêtée', cls: 'bg-stone-100 text-stone-600 border-stone-200' },
};

function StepsEditor({ steps, setSteps }) {
    const set = (i, patch) => setSteps(s => s.map((st, idx) => idx === i ? { ...st, ...patch } : st));
    return (
        <div className="space-y-3">
            {steps.map((st, i) => (
                <div key={i} className="border border-border rounded-lg p-3 space-y-2" data-testid={`seq-step-${i}`}>
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] uppercase tracking-wider font-semibold text-foreground/50">Étape {i + 1}</span>
                        <span className="text-xs text-muted-foreground">{i === 0 ? "envoyée" : 'envoyée'}</span>
                        <Input type="number" min={0} className="w-16 h-8" value={st.delay_days}
                            onChange={e => set(i, { delay_days: Math.max(0, Number(e.target.value)) })} data-testid={`seq-step-delay-${i}`} />
                        <span className="text-xs text-muted-foreground flex-1">jour(s) après {i === 0 ? "l'inscription" : `l'étape ${i}`}</span>
                        {steps.length > 1 && (
                            <button onClick={() => setSteps(s => s.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-destructive">
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    <Input placeholder="Objet de l'email (variables : {{prenom}}, {{nom}}, {{organisation}}, {{pays}})" value={st.subject}
                        onChange={e => set(i, { subject: e.target.value })} data-testid={`seq-step-subject-${i}`} />
                    <Textarea placeholder="Contenu HTML ou texte de l'email…" rows={4} value={st.body_html}
                        onChange={e => set(i, { body_html: e.target.value })} data-testid={`seq-step-body-${i}`} />
                </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setSteps(s => [...s, { ...EMPTY_STEP, delay_days: 3 }])} data-testid="seq-add-step">
                <Plus size={13} className="mr-1" /> Ajouter une étape
            </Button>
        </div>
    );
}

function SequenceDialog({ open, onClose, seq, api, onSaved }) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [filters, setFilters] = useState({});
    const [steps, setSteps] = useState([{ ...EMPTY_STEP }]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setName(seq?.name || '');
        setDescription(seq?.description || '');
        setFilters(seq?.filters || {});
        setSteps(seq?.steps?.length ? seq.steps.map(s => ({ delay_days: s.delay_days, subject: s.subject, body_html: s.body_html })) : [{ ...EMPTY_STEP }]);
    }, [open, seq]);

    const save = async () => {
        if (steps.some(s => s.subject.trim().length < 2 || s.body_html.trim().length < 2)) {
            toast.error('Chaque étape doit avoir un objet et un contenu'); return;
        }
        setSaving(true);
        try {
            const body = { name, description, filters, steps };
            if (seq) await api.put(`/network/sequences/${seq.id}`, body);
            else await api.post('/network/sequences', body);
            toast.success(seq ? 'Séquence mise à jour' : 'Séquence créée');
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setSaving(false);
    };

    const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

    return (
        <Dialog open={open} onOpenChange={v => !v && onClose()}>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="seq-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading">{seq ? 'Modifier la séquence' : 'Nouvelle séquence automatisée'}</DialogTitle>
                    <DialogDescription>Série d'emails envoyés automatiquement aux contacts inscrits, avec délais entre chaque étape.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <Input placeholder="Nom de la séquence *" value={name} onChange={e => setName(e.target.value)} data-testid="seq-name" />
                    <Input placeholder="Description (optionnel)" value={description} onChange={e => setDescription(e.target.value)} />
                    <div className="rounded-lg border border-border p-3 space-y-2">
                        <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold">Audience ciblée (filtres CRM)</div>
                        <div className="grid grid-cols-2 gap-2">
                            <select className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                                value={filters.collection || ''} onChange={e => setF('collection', e.target.value)} data-testid="seq-filter-collection">
                                <option value="">Toutes collections</option>
                                {Object.entries(NET_COLLECTIONS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                            </select>
                            <select className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                                value={filters.status_relation || ''} onChange={e => setF('status_relation', e.target.value)}>
                                <option value="">Tous statuts relation</option>
                                {Object.entries(NET_STATUSES).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                            </select>
                            <Input className="h-9" placeholder="Pays" value={filters.country || ''} onChange={e => setF('country', e.target.value)} />
                            <Input className="h-9" placeholder="Recherche libre" value={filters.q || ''} onChange={e => setF('q', e.target.value)} />
                        </div>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold mb-2">Étapes de la séquence</div>
                        <StepsEditor steps={steps} setSteps={setSteps} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button onClick={save} disabled={saving || name.trim().length < 2} data-testid="seq-save">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : 'Enregistrer'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SequenceCard({ seq, api, reload, onEdit }) {
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [busy, setBusy] = useState(false);

    const loadDetail = () => api.get(`/network/sequences/${seq.id}`).then(r => setDetail(r.data)).catch(() => {});
    useEffect(() => { if (open) loadDetail(); /* eslint-disable-next-line */ }, [open]);

    const toggleActive = async (v) => {
        try {
            await api.put(`/network/sequences/${seq.id}`, { active: v });
            toast.success(v ? 'Séquence activée — les envois démarrent automatiquement' : 'Séquence mise en pause');
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const enroll = async () => {
        setBusy(true);
        try {
            const a = await api.post(`/network/sequences/${seq.id}/audience`);
            if (!a.data.new) { toast.info(`Aucun nouveau contact (${a.data.already_enrolled} déjà inscrits)`); setBusy(false); return; }
            const r = await api.post(`/network/sequences/${seq.id}/enroll`);
            toast.success(`${r.data.enrolled} contact(s) inscrit(s) à la séquence`);
            reload(); if (open) loadDetail();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    const sendTest = async () => {
        const email = window.prompt('Envoyer un test de toutes les étapes à :');
        if (!email) return;
        try {
            const r = await api.post(`/network/sequences/${seq.id}/test`, { email });
            toast.success(`${r.data.sent} email(s) de test envoyé(s)`);
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const del = async () => {
        if (!window.confirm(`Supprimer la séquence « ${seq.name} » et toutes ses inscriptions ?`)) return;
        await api.delete(`/network/sequences/${seq.id}`);
        toast.success('Séquence supprimée');
        reload();
    };

    const stopEnrollment = async (eid) => {
        try {
            await api.patch(`/network/sequences/${seq.id}/enrollments/${eid}`);
            toast.success('Inscription arrêtée');
            loadDetail(); reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const s = seq.stats || {};
    return (
        <div className="card-soft overflow-hidden" data-testid={`seq-card-${seq.id}`}>
            <div className="p-4 flex items-center gap-3 flex-wrap">
                <button onClick={() => setOpen(o => !o)} className="shrink-0 text-muted-foreground" data-testid={`seq-expand-${seq.id}`}>
                    {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                <div className="flex-1 min-w-[200px]">
                    <div className="font-medium">{seq.name}</div>
                    <div className="text-xs text-muted-foreground">{seq.steps?.length} étape(s) · {s.total || 0} inscrit(s) · {s.emails_sent || 0} email(s) envoyé(s)</div>
                </div>
                <div className="flex items-center gap-1.5">
                    <Switch checked={!!seq.active} onCheckedChange={toggleActive} data-testid={`seq-active-${seq.id}`} />
                    <span className={`text-xs ${seq.active ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}`}>{seq.active ? 'Active' : 'En pause'}</span>
                </div>
                <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={enroll} disabled={busy} data-testid={`seq-enroll-${seq.id}`}>
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <><Users size={13} className="mr-1" /> Inscrire l'audience</>}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={onEdit} title="Modifier" data-testid={`seq-edit-${seq.id}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={sendTest} title="Envoyer un test"><FlaskConical size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={del} className="text-destructive" data-testid={`seq-delete-${seq.id}`}><Trash2 size={14} /></Button>
                </div>
            </div>
            {open && (
                <div className="border-t border-border p-4 space-y-3 bg-surface-muted/30">
                    <div className="grid sm:grid-cols-4 gap-3 text-center">
                        {[['Inscrits', s.total], ['En cours', s.active], ['Terminées', s.completed], ['Emails envoyés', s.emails_sent]].map(([l, v]) => (
                            <div key={l} className="border border-border rounded-lg p-2 bg-background">
                                <div className="font-heading text-xl text-primary">{v || 0}</div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
                            </div>
                        ))}
                    </div>
                    {!detail ? <Loader2 className="animate-spin mx-auto" size={16} /> : (
                        <div className="max-h-64 overflow-y-auto space-y-1">
                            {detail.enrollments.length === 0 && <p className="text-sm text-foreground/60 text-center py-3">Aucun contact inscrit — utilisez « Inscrire l'audience ».</p>}
                            {detail.enrollments.map(e => (
                                <div key={e.id} className="flex items-center gap-3 text-sm border border-border rounded-md px-3 py-1.5 bg-background" data-testid={`seq-enrollment-${e.id}`}>
                                    <Mail size={13} className="text-muted-foreground shrink-0" />
                                    <span className="flex-1 truncate">{e.contact?.full_name || e.email}</span>
                                    <span className="text-xs text-muted-foreground">étape {Math.min(e.current_step + 1, seq.steps.length)}/{seq.steps.length}</span>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${(ENROLL_STATUS[e.status] || ENROLL_STATUS.stopped).cls}`}>
                                        {(ENROLL_STATUS[e.status] || {}).label || e.status}
                                    </span>
                                    {e.status === 'active' && (
                                        <button onClick={() => stopEnrollment(e.id)} className="text-muted-foreground hover:text-destructive" title="Arrêter">
                                            {busy ? null : <Pause size={13} />}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function NetworkSequences({ auth }) {
    const api = useMemo(() => axios.create({ baseURL: API, headers: auth?.headers || {} }), [auth]);
    const [data, setData] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editSeq, setEditSeq] = useState(null);

    const reload = () => api.get('/network/sequences').then(r => setData(r.data)).catch(() => setData({ items: [], error: true }));
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

    if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="space-y-5" data-testid="network-sequences">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl">Séquences automatisées</h2>
                    <p className="text-sm text-foreground/60 mt-1">Drip campaigns : série d'emails envoyés automatiquement avec délais. Le worker traite les envois toutes les 60 secondes.</p>
                </div>
                <Button onClick={() => { setEditSeq(null); setDialogOpen(true); }} data-testid="seq-new-btn">
                    <Plus size={14} className="mr-1.5" /> Nouvelle séquence
                </Button>
            </div>
            {!data.smtp_configured && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm px-4 py-3">
                    SMTP non configuré — les séquences ne pourront pas être activées.
                </div>
            )}
            {data.items.length === 0 ? (
                <div className="card-soft p-12 text-center text-foreground/60" data-testid="seq-empty">
                    <Mail size={28} className="mx-auto mb-3 text-muted-foreground" />
                    Aucune séquence. Créez votre première séquence d'accueil ou de relance.
                </div>
            ) : (
                <div className="space-y-3">
                    {data.items.map(seq => (
                        <SequenceCard key={seq.id} seq={seq} api={api} reload={reload}
                            onEdit={() => { setEditSeq(seq); setDialogOpen(true); }} />
                    ))}
                </div>
            )}
            <SequenceDialog open={dialogOpen} onClose={() => setDialogOpen(false)} seq={editSeq} api={api} onSaved={reload} />
        </div>
    );
}
